/**
 * Состояние журнала, которое живёт В АДРЕСЕ.
 *
 * ТЗ §4: значимый под-экран имеет свой адрес, состояние не хранится вкладками. Для журнала
 * это означает, что день, филиал, шаг сетки, фильтр мастеров и режим показа — параметры
 * строки запроса. Ссылку на «16 сентября, филиал на Ленина, шаг 15» можно прислать
 * в чате, открыть в соседней вкладке и вернуться к ней кнопкой «назад».
 */

/** Шаг сетки в минутах. Три значения из макета, четвёртого нет. */
export const JOURNAL_STEPS = [5, 15, 30] as const
export type JournalStep = (typeof JOURNAL_STEPS)[number]

export const JOURNAL_VIEWS = ["day", "week", "master"] as const
export type JournalView = (typeof JOURNAL_VIEWS)[number]

/**
 * Под-экраны панели записи. У каждого СВОЙ адрес — это правило проекта, а не удобство:
 * ссылку «вот эта запись, вот этот диалог» отправляют в поддержку, и она обязана
 * открыться у того, кому её прислали.
 */
export const JOURNAL_DIALOGS = ["cancel", "no-show", "reschedule", "block"] as const
export type JournalDialog = (typeof JOURNAL_DIALOGS)[number]

/** Мастер в адресе слота. «none» — колонка «Без мастера»: это выбор, а не пустота. */
export const SLOT_NO_MASTER = "none"

export interface JournalSearch {
  /** Календарная дата филиала, YYYY-MM-DD. Пусто = сегодня по времени филиала. */
  readonly date?: string | undefined
  readonly branch?: string | undefined
  readonly step?: JournalStep | undefined
  readonly masters?: readonly string[] | undefined
  readonly cancelled?: boolean | undefined
  readonly view?: JournalView | undefined
  /**
   * Мастер, чей день показан лентой. Это НЕ фильтр `masters`, а фокус: на телефоне
   * сетка заменяется списком одного мастера (ТЗ §2.13), и выбранный мастер обязан жить
   * в адресе — иначе ссылка «посмотри день Ани» открывает чужой день.
   */
  readonly master?: string | undefined
  /**
   * Проверка приёмки: сетка в градациях серого. Шести статусам положено оставаться
   * различимыми без цвета (ТЗ §2.3.3), и ссылка на эту проверку должна пересылаться
   * в чате так же, как ссылка на день. Кнопки в интерфейсе у неё нет намеренно:
   * администратору она не нужна ни разу за смену.
   */
  readonly check?: "gray" | undefined
  /**
   * Открытая панель записи (2-2.3.4). Адрес, а не состояние компонента: администратор
   * пересылает ссылку на запись в поддержку, открывает её в соседней вкладке и
   * возвращается кнопкой «назад».
   */
  readonly appointment?: string | undefined
  /** Открытый диалог панели: отмена, неявка, перенос. */
  readonly dialog?: JournalDialog | undefined
  /** Время нового слота «ЧЧ:ММ» — быстрая форма 2-2.7.1 открыта на этом времени. */
  readonly slotAt?: string | undefined
  /** Мастер нового слота: идентификатор либо «none» для колонки «Без мастера». */
  readonly slotMaster?: string | undefined
  /**
   * ПРОВЕРКА ПРИЁМКИ: режим деградации платформы (?degraded=read_only|maintenance).
   *
   * Кнопки в интерфейсе у неё нет намеренно — это не настройка салона, а состояние
   * платформы. Сегодня API его не сообщает вовсе (ни в контексте кабинета, ни
   * заголовком, ни в /health/ready), поэтому проверить макеты «технические работы»
   * можно только адресом. Как только сервер начнёт отвечать 503 с кодом read_only
   * или maintenance, режим включится сам, без этого параметра.
   */
  readonly degraded?: "read_only" | "maintenance" | undefined
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function asDate(value: unknown): string | undefined {
  return typeof value === "string" && ISO_DATE.test(value) ? value : undefined
}

function asStep(value: unknown): JournalStep | undefined {
  const step = typeof value === "string" ? Number.parseInt(value, 10) : value
  return JOURNAL_STEPS.find((known) => known === step)
}

function asView(value: unknown): JournalView | undefined {
  return JOURNAL_VIEWS.find((known) => known === value)
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value
  if (value === "true" || value === "1") return true
  if (value === "false" || value === "0") return false
  return undefined
}

function asDialog(value: unknown): JournalDialog | undefined {
  return JOURNAL_DIALOGS.find((known) => known === value)
}

/** «14:30». Чужое значение отбрасывается: форма откроется по клику, а не по мусору. */
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/

function asClock(value: unknown): string | undefined {
  return typeof value === "string" && CLOCK.test(value) ? value : undefined
}

function asIdList(value: unknown): readonly string[] | undefined {
  if (Array.isArray(value)) {
    const ids = value.filter((item): item is string => typeof item === "string" && item.length > 0)
    return ids.length > 0 ? ids : undefined
  }
  if (typeof value === "string" && value.length > 0) return value.split(",").filter(Boolean)
  return undefined
}

/**
 * Разбор параметров адреса.
 *
 * Значение приходит снаружи: его мог набрать человек или испортить чат при пересылке.
 * Поэтому непонятное значение не роняет экран, а просто отбрасывается — журнал откроется
 * на сегодняшнем дне, а не на белом экране с ошибкой.
 */
export function parseJournalSearch(raw: Record<string, unknown>): JournalSearch {
  return {
    date: asDate(raw["date"]),
    branch: typeof raw["branch"] === "string" ? raw["branch"] : undefined,
    step: asStep(raw["step"]),
    masters: asIdList(raw["masters"]),
    cancelled: asBoolean(raw["cancelled"]),
    view: asView(raw["view"]),
    master: typeof raw["master"] === "string" && raw["master"].length > 0 ? raw["master"] : undefined,
    check: raw["check"] === "gray" ? "gray" : undefined,
    appointment:
      typeof raw["appointment"] === "string" && raw["appointment"].length > 0
        ? raw["appointment"]
        : undefined,
    dialog: asDialog(raw["dialog"]),
    slotAt: asClock(raw["slotAt"]),
    slotMaster:
      typeof raw["slotMaster"] === "string" && raw["slotMaster"].length > 0
        ? raw["slotMaster"]
        : undefined,
    degraded:
      raw["degraded"] === "read_only" || raw["degraded"] === "maintenance"
        ? raw["degraded"]
        : undefined,
  }
}
